import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { LoadingDialog } from "@/components/ui/loading-dialog"
import { useToast } from "@/components/ui/use-toast"
import { Download, RefreshCw, ImageIcon, Sun, Aperture, PenTool, Pencil, Box } from "lucide-react"

interface VolunteerDetails {
  name: string
  achievement: string
  impact: string
  duration: string
  quote: string
  mood: string
}

interface ImageStyle {
  id: string
  name: string
  description: string
  category: "realistic_image" | "digital_illustration"
  variant?: string
  icon: JSX.Element
}

interface ImageGenerationResponse {
  data: {
    imageUrl: string
  }
  message: string
}

const imageStyles: ImageStyle[] = [
  {
    id: "realistic_image",
    name: "Realistic Photo",
    description: "Professional, realistic photography style",
    category: "realistic_image",
    icon: <ImageIcon className="h-5 w-5 text-blue-500" />
  },
  {
    id: "realistic_image/natural_light",
    name: "Natural Light",
    description: "Soft, natural lighting for authentic feel",
    category: "realistic_image",
    variant: "natural_light",
    icon: <Sun className="h-5 w-5 text-yellow-500" />
  },
  {
    id: "realistic_image/studio_portrait",
    name: "Studio Style",
    description: "Professional studio quality look",
    category: "realistic_image",
    variant: "studio_portrait",
    icon: <Aperture className="h-5 w-5 text-purple-500" />
  },
  {
    id: "digital_illustration/2d_art_poster",
    name: "2D Art Poster",
    description: "Modern, artistic poster design",
    category: "digital_illustration",
    variant: "2d_art_poster",
    icon: <PenTool className="h-5 w-5 text-green-500" />
  },
  {
    id: "digital_illustration/hand_drawn",
    name: "Hand Drawn",
    description: "Artistic hand-drawn illustration style",
    category: "digital_illustration",
    variant: "hand_drawn",
    icon: <Pencil className="h-5 w-5 text-orange-500" />
  },
  {
    id: "digital_illustration/handmade_3d",
    name: "3D Style",
    description: "Modern 3D illustration look",
    category: "digital_illustration",
    variant: "handmade_3d",
    icon: <Box className="h-5 w-5 text-indigo-500" />
  }
]

const moodOptions = [
  { id: "inspiring", name: "Inspiring", description: "Motivational and uplifting" },
  { id: "grateful", name: "Grateful", description: "Expressing thankfulness" },
  { id: "celebratory", name: "Celebratory", description: "Festive and congratulatory" },
  { id: "professional", name: "Professional", description: "Formal recognition" }
]

const aspectRatios = [
  { id: "1:1", name: "Square", description: "Perfect for Instagram posts", dimensions: "1080x1080" },
  { id: "16:9", name: "Wide", description: "Ideal for LinkedIn/Facebook", dimensions: "1920x1080" },
  { id: "9:16", name: "Story", description: "Great for Instagram/Facebook stories", dimensions: "1080x1920" },
  { id: "4:5", name: "Portrait", description: "Optimized for Instagram feed", dimensions: "1080x1350" }
]

const generatePrompt = (style: ImageStyle, details: VolunteerDetails) => {
  return `Create a recognition post for a volunteer. 
  Highlight ${details.name} who ${details.achievement}. 
  Their impact: ${details.impact}. 
  ${details.quote ? `Featuring their quote: "${details.quote}"` : ''} 
  Create a ${details.mood} atmosphere that celebrates their contribution. Include modern design elements, typography, and ensure the image is suitable for social media recognition.`
}

export default function VolunteerRecognitionPage({ profile, session }: { profile: any | null, session: any | null }) {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [generatedImages, setGeneratedImages] = useState<string[]>([])
  const [currentImage, setCurrentImage] = useState<string | null>(null)
  const [selectedStyle, setSelectedStyle] = useState("")
  const [selectedRatio, setSelectedRatio] = useState("1:1")
  const [selectedMood, setSelectedMood] = useState("inspiring")
  const [regeneratingIndex, setRegeneratingIndex] = useState<number | null>(null)

  const [volunteerDetails, setVolunteerDetails] = useState<VolunteerDetails>({
    name: "",
    achievement: "",
    impact: "",
    duration: "",
    quote: "",
    mood: "inspiring"
  })

  const handleGenerateImages = async () => {
    if (!selectedStyle || !volunteerDetails.name || !volunteerDetails.achievement) {
      toast({
        title: "Missing Information",
        description: "Please provide volunteer's name, achievement, and select a style",
        variant: "destructive"
      })
      return
    }

    setLoading(true)
    try {
      const style = imageStyles.find(s => s.id === selectedStyle)!
      const prompt = generatePrompt(style, volunteerDetails)

      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          prompt,
          size: selectedRatio,
          style: selectedStyle,
          imageType: imageStyles.find(s => s.id === selectedStyle)?.category || "realistic_image"
        })
      })

      if (!response.ok) {
        throw new Error('Failed to generate images')
      }

      const data = await response.json() as ImageGenerationResponse
      setCurrentImage(data.data.imageUrl)
      setGeneratedImages(prev => [...prev, data.data.imageUrl])
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate images. Please try again.",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRegenerate = async (index: number) => {
    setRegeneratingIndex(index)
    try {
      const style = imageStyles.find(s => s.id === selectedStyle)!
      const prompt = generatePrompt(style, volunteerDetails)

      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          prompt,
          size: selectedRatio,
          style: selectedStyle,
          imageType: imageStyles.find(s => s.id === selectedStyle)?.category || "realistic_image"
        })
      })

      if (!response.ok) {
        throw new Error('Failed to regenerate image')
      }

      const data = await response.json() as ImageGenerationResponse
      const newImages = [...generatedImages]
      newImages[index] = data.data.imageUrl
      setGeneratedImages(newImages)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to regenerate image. Please try again.",
        variant: "destructive"
      })
    } finally {
      setRegeneratingIndex(null)
    }
  }

  const handleDownload = async (url: string) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `volunteer-recognition-${Date.now()}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download image. Please try again.",
        variant: "destructive"
      })
    }
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Volunteer Recognition Posts</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6 space-y-4">
          <h2 className="text-xl font-semibold mb-4">Volunteer Details</h2>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Volunteer Name</Label>
              <Input
                id="name"
                value={volunteerDetails.name}
                onChange={(e) => setVolunteerDetails(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter volunteer's name"
              />
            </div>

            <div>
              <Label htmlFor="achievement">Key Achievement</Label>
              <Input
                id="achievement"
                value={volunteerDetails.achievement}
                onChange={(e) => setVolunteerDetails(prev => ({ ...prev, achievement: e.target.value }))}
                placeholder="e.g., Led 5 community workshops"
              />
            </div>

            <div>
              <Label htmlFor="impact">Impact Statement</Label>
              <Textarea
                id="impact"
                value={volunteerDetails.impact}
                onChange={(e) => setVolunteerDetails(prev => ({ ...prev, impact: e.target.value }))}
                placeholder="Describe the volunteer's impact on the community"
              />
            </div>

            <div>
              <Label htmlFor="quote">Inspirational Quote (Optional)</Label>
              <Textarea
                id="quote"
                value={volunteerDetails.quote}
                onChange={(e) => setVolunteerDetails(prev => ({ ...prev, quote: e.target.value }))}
                placeholder="Add a meaningful quote from the volunteer"
              />
            </div>
          </div>
        </Card>

        <Card className="p-6 space-y-4">
          <h2 className="text-xl font-semibold mb-4">Design Options</h2>
          
          <div className="space-y-4">
            <div>
              <Label>Visual Style</Label>
              <RadioGroup
                value={selectedStyle}
                onValueChange={setSelectedStyle}
                className="grid grid-cols-2 gap-4 pt-2"
              >
                {imageStyles.map((style) => (
                  <div key={style.id}>
                    <RadioGroupItem
                      value={style.id}
                      id={style.id}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={style.id}
                      className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                    >
                      {style.icon}
                      <div className="text-sm font-semibold">{style.name}</div>
                      <div className="text-xs text-muted-foreground">{style.description}</div>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label>Post Format</Label>
              <RadioGroup
                value={selectedRatio}
                onValueChange={setSelectedRatio}
                className="grid grid-cols-2 gap-4 pt-2"
              >
                {aspectRatios.map((ratio) => (
                  <div key={ratio.id}>
                    <RadioGroupItem
                      value={ratio.id}
                      id={ratio.id}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={ratio.id}
                      className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                    >
                      <div className="text-sm font-semibold">{ratio.name}</div>
                      <div className="text-xs text-muted-foreground">{ratio.description}</div>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label>Recognition Mood</Label>
              <Select value={selectedMood} onValueChange={setSelectedMood}>
                <SelectTrigger>
                  <SelectValue placeholder="Select mood" />
                </SelectTrigger>
                <SelectContent>
                  {moodOptions.map((mood) => (
                    <SelectItem key={mood.id} value={mood.id}>
                      {mood.name} - {mood.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleGenerateImages}
              className="w-full"
              size="lg"
              disabled={loading}
            >
              {loading ? "Generating..." : "Generate Recognition Posts"}
            </Button>
          </div>
        </Card>
      </div>

      {loading && <LoadingDialog open={loading} />}

      {generatedImages.length > 0 && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Generated Recognition Posts</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {generatedImages.map((image, index) => (
              <div key={index} className="relative group">
                <img
                  src={image}
                  alt={`Recognition post ${index + 1}`}
                  className="w-full rounded-lg shadow-lg"
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 rounded-lg">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleDownload(image)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleRegenerate(index)}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Regenerate
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}
